/**
 * Regras partilhadas de PREÇO (#474 / #479): preço base derivado das
 * variantes, extras em percentagem e a política de pratos com todas as
 * variantes indisponíveis.
 *
 * `basePriceFromVariants` é usada pelo commit do import (o «a partir de»
 * acompanha as variantes que o dono editou na revisão), pela revisão no
 * painel (selo «Confirmar preço», filtro «Por confirmar» e contagem do
 * toast) e, desde o #479, pelo catálogo: criar/editar/apagar variantes
 * recalcula `products.price_cents` no servidor com a MESMA regra.
 */

/** Variante como a regra do base a vê: preço e (opcional) disponibilidade. */
export type VariantPriceLike = {
  price_cents: number
  /** ausente = disponível (drafts do import não têm o campo) */
  is_available?: boolean | undefined
}

/**
 * Preço base DERIVADO das variantes (#474/#479, regra única):
 *
 * 1. mínimo do `price_cents` das variantes DISPONÍVEIS com preço > 0;
 * 2. sem nenhuma disponível com preço: mínimo de TODAS as com preço > 0
 *    (valor coerente; com a política `not_orderable` o prato não é
 *    pedível, ver `productOrderableViaBase`);
 * 3. sem NENHUMA com preço: `null` — o base NÃO muda.
 *
 * Extras nunca contam (são adicionais que se somam, não alternativas).
 * PURA: não muta o argumento.
 */
export function basePriceFromVariants(variants: ReadonlyArray<VariantPriceLike>): number | null {
  const priced = variants.filter((v) => v.price_cents > 0)
  const available = priced.filter((v) => v.is_available !== false)
  if (available.length > 0) return Math.min(...available.map((v) => v.price_cents))
  if (priced.length > 0) return Math.min(...priced.map((v) => v.price_cents))
  return null
}

/**
 * Extra em percentagem (#479, decisão PO): incide sobre o PREÇO DE LISTA da
 * linha — a variante escolhida quando há, senão o base do produto — e não
 * sobre o base do produto quando o cliente escolheu uma variante. Mesma
 * fórmula na API (`orders/calculation.ts`) e no preview do Menu
 * (`ProductDetail`): a paridade vem de usar este helper.
 */
export function percentExtraCents(listUnitCents: number, priceValue: number): number {
  return Math.round((listUnitCents * priceValue) / 10000)
}

/** Política para prato com variantes e NENHUMA disponível (#479, decisão PO). */
export type AllVariantsUnavailablePolicy = 'not_orderable' | 'charge_base'

/**
 * #479 (decisão PO 25/10): `not_orderable` por omissão — o prato sai da
 * lista do Menu e o pedido é recusado (mesmo tratamento de
 * `is_available=false`), em vez de cobrar em silêncio o preço de uma
 * variante que a cozinha não tem. `charge_base` fica como opção (comportamento
 * anterior); mudar é trocar esta constante.
 */
export const ALL_VARIANTS_UNAVAILABLE_POLICY: AllVariantsUnavailablePolicy = 'not_orderable'

/**
 * O prato é pedível? (#479) — usado pela lista do `/menu` e pela validação
 * do pedido:
 * - sem variantes: pedível (preço direto do produto);
 * - com alguma variante disponível: pedível (a escolha é exigida à parte,
 *   `VARIANT_REQUIRED`);
 * - com variantes e NENHUMA disponível: depende da política — `not_orderable`
 *   (não pedível) ou `charge_base` (pedível ao base).
 */
export function productOrderable(
  hasVariants: boolean,
  anyVariantAvailable: boolean,
  policy: AllVariantsUnavailablePolicy = ALL_VARIANTS_UNAVAILABLE_POLICY,
): boolean {
  if (!hasVariants) return true
  if (anyVariantAvailable) return true
  return policy === 'charge_base'
}
